import { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography
} from "@mui/material";
import { HallazgosValidacionList } from "./HallazgosValidacionList";

/**
 * Diálogo de confirmación para una acción irreversible y de alto riesgo: una
 * transición de estado de concurso, o habilitar una temporada nueva. Muestra
 * siempre todo el contexto relevante antes de pedir confirmación — nunca
 * "confirmá y después te digo si había un problema" — y maneja el propio ciclo
 * de vida de la confirmación (en progreso / error con reintentar / éxito).
 *
 * `requiereConfirmacionReforzada` agrega fricción deliberada (pensado para
 * Liguilla → Noche de fallos: ocurre en vivo, bajo presión, y un tap accidental
 * no debe poder dispararla) — hay que tipear `textoConfirmacion` para habilitar
 * el botón.
 */
export const TransicionEstadoDialog = ({
  open,
  onClose,
  titulo,
  acciones = [],
  hallazgos = [],
  cantidadErrores = 0,
  cantidadAdvertencias = 0,
  puedeConfirmar = false,
  requiereConfirmacionReforzada = false,
  textoConfirmacion = "CONFIRMAR",
  mensajeExito = "Listo.",
  onConfirmar
}) => {
  const [estado, setEstado] = useState("idle"); // idle | confirmando | error | exito
  const [mensajeError, setMensajeError] = useState("");
  const [textoIngresado, setTextoIngresado] = useState("");

  const cerrar = () => {
    if (estado === "confirmando") return;
    setEstado("idle");
    setMensajeError("");
    setTextoIngresado("");
    onClose?.();
  };

  const confirmar = async () => {
    setEstado("confirmando");
    setMensajeError("");
    try {
      await onConfirmar?.();
      setEstado("exito");
    } catch (err) {
      setMensajeError(
        err?.response?.data?.message ||
          err?.message ||
          "Ocurrió un error inesperado. El estado no cambió."
      );
      setEstado("error");
    }
  };

  const reforzadaHabilitada =
    !requiereConfirmacionReforzada || textoIngresado.trim() === textoConfirmacion;
  const puedeApretarConfirmar =
    puedeConfirmar && reforzadaHabilitada && estado !== "confirmando";

  return (
    <Dialog open={open} onClose={cerrar} maxWidth="sm" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent>
        {estado === "exito" ? (
          <Alert severity="success">{mensajeExito}</Alert>
        ) : (
          <>
            {acciones.length > 0 && (
              <>
                <Typography variant="subtitle2">Qué va a pasar:</Typography>
                <List dense>
                  {acciones.map((accion, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText primary={accion} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            <HallazgosValidacionList
              hallazgos={hallazgos}
              cantidadErrores={cantidadErrores}
              cantidadAdvertencias={cantidadAdvertencias}
              mensajeVacio="No hay hallazgos que bloqueen esta acción."
            />

            {requiereConfirmacionReforzada && (
              <TextField
                fullWidth
                margin="normal"
                label={`Escribí "${textoConfirmacion}" para confirmar`}
                value={textoIngresado}
                onChange={e => setTextoIngresado(e.target.value)}
                disabled={estado === "confirmando"}
                size={requiereConfirmacionReforzada ? "medium" : "small"}
              />
            )}

            {estado === "confirmando" && (
              <Typography sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                <CircularProgress size={18} /> Procesando, no cierres esta ventana…
              </Typography>
            )}

            {estado === "error" && <Alert severity="error">{mensajeError}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {estado === "exito" ? (
          <Button onClick={cerrar} variant="contained">
            Cerrar
          </Button>
        ) : (
          <>
            <Button onClick={cerrar} disabled={estado === "confirmando"}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color={requiereConfirmacionReforzada ? "error" : "primary"}
              size={requiereConfirmacionReforzada ? "large" : "medium"}
              disabled={!puedeApretarConfirmar}
              onClick={confirmar}
            >
              {estado === "error" ? "Reintentar" : "Confirmar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransicionEstadoDialog;
